<template>
  <div class="mypage-ticket-today-summary-section">
    <p class="mypage-ticket-today-summary-section__title">{{ ticketEvent.eventName }}</p>

    <p class="mypage-ticket-today-summary-section__place">
      {{ displayPlaceName }}
    </p>

    <p class="mypage-ticket-today-summary-section__date">
      {{ displayEventDate }} {{ displayEventDayOfWeek }}
    </p>

    <p class="mypage-ticket-today-summary-section__time">
      <span class="mypage-ticket-today-summary-section__time-label">{{
        $tc('MypagePage.MypageTicketTodayPage.openingTitle')
      }}</span>
      <span class="mypage-ticket-today-summary-section__time-data">{{ displayOpeningTime }}</span>
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import DigitalTicketDataDocument from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataDocument'
import useDisplayTicketData from '@/components/MypageTicketListPage/hook/useDisplayTicketData'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'
import usePurchasedTicketData from '@/components/hook/ticket/usePurchasedTicketData'

export default defineComponent({
  name: 'MypageTicketTodaySummarySection',
  props: {
    /**
     * デジタルチケット イベント情報
     */
    ticketEvent: {
      type: Object as PropType<DigitalTicketDataDocument>,
      required: true,
    },
  },
  setup(props) {
    const { getDisplayPlaceName } = useDisplayTicketData()
    const { getDisplayDateJa } = useDisplayDependingOnLang()
    const { matchedTodayDate } = usePurchasedTicketData()

    /**
     * 場所
     */
    const displayPlaceName = computed(() => getDisplayPlaceName(props.ticketEvent))

    /**
     * イベント期間開始日
     */
    const openingTodayInfo = computed(() => matchedTodayDate(props.ticketEvent))

    /**
     * イベント開催日
     */
    const displayEventDate = computed(() =>
      openingTodayInfo.value.key
        ? getDisplayDateJa(props.ticketEvent.openingDates?.[openingTodayInfo.value.key])
        : '',
    )

    /**
     * イベント開催日の曜日
     */
    const displayEventDayOfWeek = computed(() =>
      openingTodayInfo.value.key
        ? getDisplayDateJa(
            props.ticketEvent.openingDates?.[openingTodayInfo.value.key],
            'ddd',
            'ddd',
          )
        : '',
    )

    /**
     * 開場時間
     */
    const displayOpeningTime = computed(() =>
      openingTodayInfo.value.key
        ? getDisplayDateJa(
            props.ticketEvent.openingDates?.[openingTodayInfo.value.key],
            'HH:mm',
            'HH:mm',
          )
        : '',
    )

    return {
      displayPlaceName,
      displayEventDate,
      displayEventDayOfWeek,
      displayOpeningTime,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-ticket-today-summary-section {
  padding: 24px;
  text-align: left;
  background-color: $white;
  border-radius: 10px;

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: $black80;
    letter-spacing: 0.04em;
  }

  &__place {
    font-size: 12px;
    color: $gray75;
  }

  &__date {
    font-size: 12px;
    font-weight: 500;
    color: $black20;
    letter-spacing: 0.04em;
  }

  &__time {
    font-size: 12px;
    color: $black20;

    &-label {
      font-size: 12px;
    }

    &-data {
      position: relative;
      display: inline-block;
      width: 50px;
      margin-left: 18px;
      font-size: 12px;
      letter-spacing: 0.04em;
      text-align: right;

      &::before {
        position: absolute;
        top: 50%;
        left: -4px;
        width: 2px;
        height: 8px;
        content: '';
        background: linear-gradient(
          84.94deg,
          #00ffba -121.14%,
          #00ffba -121.14%,
          rgba(0, 255, 186, 0.5) -80.24%,
          #00ffba -34.6%,
          rgba(0, 255, 186, 0.5) 10.88%,
          #00ffba 51.93%,
          rgba(0, 255, 186, 0.5) 91.87%
        );
        transform: translateY(-50%);
      }
    }
  }
}
</style>
